import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { inputs, references, whyDiffer } from './data/teaser';
import { chapterFor } from './data/journey';
import { embed, irreversible, proactive, snak2Disclaimer, snak2Rail, snak2References, windowOfOpportunity } from './data/snak2';
import { fitToClinic, pilot, predict, principles, snak3Disclaimer, snak3Rail, snak3References } from './data/snak3';
import { PhoneFrame } from './components/PhoneFrame';
import { StrategyOverlay } from './components/StrategyOverlay';
import { JourneyOverlay } from './components/JourneyOverlay';
import { EntryScreen } from './components/screens/EntryScreen';
import { PromiseScreen } from './components/screens/PromiseScreen';
import { WhyDifferScreen } from './components/screens/WhyDifferScreen';
import { InputsScreen } from './components/screens/InputsScreen';
import { PhenotypesScreen } from './components/screens/PhenotypesScreen';
import { BridgeScreen } from './components/screens/BridgeScreen';
import { WindowScreen } from './components/screens/snak2/WindowScreen';
import { IrreversibilityScreen } from './components/screens/snak2/IrreversibilityScreen';
import { ProactiveScreen } from './components/screens/snak2/ProactiveScreen';
import { EmbedScreen } from './components/screens/snak2/EmbedScreen';
import { PreventScreen } from './components/screens/snak2/PreventScreen';
import { PrinciplesScreen } from './components/screens/snak3/PrinciplesScreen';
import { FitToClinicScreen } from './components/screens/snak3/FitToClinicScreen';
import { PilotScreen } from './components/screens/snak3/PilotScreen';
import { PredictScreen } from './components/screens/snak3/PredictScreen';
import { CoCreateScreen } from './components/screens/snak3/CoCreateScreen';
const EASE = [0.23, 1, 0.32, 1] as const;
type Snak = 1 | 2 | 3;
type Mode = 'menu' | 'campaign' | 'presentation';
interface ScreenDef {
  id: string;
  snak: Snak;
  nextLabel?: string;
  footnote?: string;
}
const ALL_SCREENS: ScreenDef[] = [{
  id: 'promise',
  snak: 1,
  nextLabel: 'Explore the evidence',
  footnote: references.intro
}, {
  id: 'whyDiffer',
  snak: 1,
  nextLabel: whyDiffer.cta,
  footnote: references.midena
}, {
  id: 'inputs',
  snak: 1,
  nextLabel: inputs.cta,
  footnote: references.munk
}, {
  id: 'phenotypes',
  snak: 1,
  footnote: references.midena
}, {
  id: 'bridge',
  snak: 1
}, {
  id: 's2-window',
  snak: 2,
  nextLabel: windowOfOpportunity.cta,
  footnote: snak2References.window
}, {
  id: 's2-irreversible',
  snak: 2,
  nextLabel: irreversible.cta,
  footnote: snak2References.irreversible
}, {
  id: 's2-proactive',
  snak: 2,
  nextLabel: proactive.cta,
  footnote: snak2References.proactive
}, {
  id: 's2-embed',
  snak: 2,
  nextLabel: embed.cta,
  footnote: snak2References.embed
}, {
  id: 's2-prevent',
  snak: 2,
  footnote: snak2References.prevent
}, {
  id: 's3-principles',
  snak: 3,
  nextLabel: principles.cta,
  footnote: snak3References.principles
}, {
  id: 's3-fit',
  snak: 3,
  nextLabel: fitToClinic.cta,
  footnote: snak3References.fitToClinic
}, {
  id: 's3-pilot',
  snak: 3,
  nextLabel: pilot.cta,
  footnote: snak3References.pilot
}, {
  id: 's3-predict',
  snak: 3,
  nextLabel: predict.cta,
  footnote: snak3References.predict
}, {
  id: 's3-cocreate',
  snak: 3,
  footnote: snak3References.coCreate
}];
function railFor(snak: Snak) {
  if (snak === 3) {
    return {
      chapter: snak3Rail.chapter,
      railEyebrow: snak3Rail.eyebrow,
      railTitle: snak3Rail.title,
      railBlurb: snak3Rail.blurb,
      railDisclaimer: snak3Disclaimer
    };
  }
  if (snak === 2) {
    return {
      chapter: snak2Rail.chapter,
      railEyebrow: snak2Rail.eyebrow,
      railTitle: snak2Rail.title,
      railBlurb: snak2Rail.blurb,
      railDisclaimer: snak2Disclaimer
    };
  }
  return {
    chapter: 'Snak 1'
  };
}
export function App() {
  const [mode, setMode] = useState<Mode>('menu');
  const [activeSnak, setActiveSnak] = useState<Snak>(1);
  const [step, setStep] = useState(0);
  const [strategyOpen, setStrategyOpen] = useState(false);
  const [journeyOpen, setJourneyOpen] = useState(false);
  const openStrategy = () => setStrategyOpen(true);
  const openJourney = () => setJourneyOpen(true);
  const goHome = () => {
    setMode('menu');
    setStep(0);
  };
  const startCampaign = (snak: Snak) => {
    setActiveSnak(snak);
    setMode('campaign');
    setStep(0);
  };
  const startPresentation = () => {
    setMode('presentation');
    setStep(0);
  };
  if (mode === 'menu') {
    return <div className="min-h-full w-full bg-mist font-sans" data-id="element-0" data-mp="14uu0f40txreh">
        <PhoneFrame step={0} total={0} chapter="Entry" railTitle={['Precision', 'phenotyping.']} railEyebrow="Campaign architecture · three archetypes" railBlurb="Pick a single instalment to run as a standalone campaign, or run all three in sequence as one face-to-face presentation." modeLabel="Entry menu" onOpenStrategy={openStrategy} onOpenJourney={openJourney} data-id="element-1" data-mp="zwsl8u6iyc8a">
          <EntryScreen onStartCampaign={startCampaign} onStartPresentation={startPresentation} onOpenJourney={openJourney} data-id="element-2" data-mp="205qugxg084xd" />
        </PhoneFrame>

        <AnimatePresence data-id="element-3" data-mp="29vtp122rxqxt">
          {strategyOpen && <StrategyOverlay onClose={() => setStrategyOpen(false)} data-id="element-4" data-mp="29wljuaojrkn1" />}
        </AnimatePresence>
        <AnimatePresence data-id="element-5" data-mp="2etv7bksdlm88">
          {journeyOpen && <JourneyOverlay onClose={() => setJourneyOpen(false)} data-id="element-6" data-mp="2ex96yl9nnhbz" />}
        </AnimatePresence>
      </div>;
  }
  const screens = mode === 'presentation' ? ALL_SCREENS : ALL_SCREENS.filter(s => s.snak === activeSnak);
  const screen = screens[Math.min(step, screens.length - 1)];
  const chapter = chapterFor(screen.snak);
  const isLast = step === screens.length - 1;
  const nextScreen = screens[step + 1];
  const crossesChapter = nextScreen && nextScreen.snak !== screen.snak;
  const next = () => setStep(s => Math.min(s + 1, screens.length - 1));
  const back = () => setStep(s => Math.max(s - 1, 0));
  const restart = () => setStep(0);
  const chapterScreens = screens.filter(s => s.snak === screen.snak);
  const chapterStep = chapterScreens.findIndex(s => s.id === screen.id);
  const nav = {
    onBack: step > 0 ? back : undefined,
    onNext: isLast ? undefined : next,
    nextLabel: crossesChapter ? `Continue to ${chapterFor(nextScreen.snak).label}` : screen.nextLabel,
    footnote: screen.footnote
  };
  return <div className="min-h-full w-full bg-mist font-sans" data-id="element-7" data-mp="2fqaf9aivsg0n">
      <PhoneFrame step={chapterStep} total={chapterScreens.length} brand={chapter.brand} onHome={goHome} modeLabel={mode === 'presentation' ? `Presentation · ${step + 1} of ${screens.length}` : `${chapter.archetype} campaign`} onOpenStrategy={openStrategy} onOpenJourney={openJourney} {...railFor(screen.snak)} {...nav} data-id="element-8" data-mp="2tiqj2djlwi2m">
        <AnimatePresence mode="wait" data-id="element-9" data-mp="3i4y06p6aq4xw">
          <motion.div key={screen.id} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.22,
          ease: EASE
        }} className="flex flex-1 flex-col" data-id="element-10" data-mp="3i1sab7puez14">
            {screen.id === 'promise' && <PromiseScreen data-id="element-11" data-mp="2zd58qz6vkrsa" />}
            {screen.id === 'whyDiffer' && <WhyDifferScreen data-id="element-12" data-mp="2zbke79kk98uf" />}
            {screen.id === 'inputs' && <InputsScreen onAdvance={next} data-id="element-13" data-mp="344di5v87gd5g" />}
            {screen.id === 'phenotypes' && <PhenotypesScreen data-id="element-14" data-mp="2pez7mjsw7poh" />}
            {screen.id === 'bridge' && <BridgeScreen onRestart={restart} data-id="element-15" data-mp="2pgip2dqac6ru" />}
            {screen.id === 's2-window' && <WindowScreen data-id="element-16" data-mp="2uf3nc7lz8gdn" />}
            {screen.id === 's2-irreversible' && <IrreversibilityScreen data-id="element-17" data-mp="2gej5bdnvyol7" />}
            {screen.id === 's2-proactive' && <ProactiveScreen data-id="element-18" data-mp="2ggdr0u6zekqc" />}
            {screen.id === 's2-embed' && <EmbedScreen data-id="element-19" data-mp="2ketborflbh0l" />}
            {screen.id === 's2-prevent' && <PreventScreen onRestart={mode === 'campaign' ? goHome : undefined} data-id="element-20" data-mp="30vx35tktntk7" />}
            {screen.id === 's3-principles' && <PrinciplesScreen data-id="element-21" data-mp="2vxlu3l2cpn31" />}
            {screen.id === 's3-fit' && <FitToClinicScreen data-id="element-22" data-mp="2qwxre24rfhsz" />}
            {screen.id === 's3-pilot' && <PilotScreen data-id="element-23" data-mp="2qvczkk9tycyt" />}
            {screen.id === 's3-predict' && <PredictScreen data-id="element-24" data-mp="2lyw8bh7c3ihi" />}
            {screen.id === 's3-cocreate' && <CoCreateScreen onRestart={goHome} data-id="element-25" data-mp="2hwj4iyc5dund" />}
          </motion.div>
        </AnimatePresence>
      </PhoneFrame>

      <AnimatePresence data-id="element-26" data-mp="2d62qbc0i64zy">
        {strategyOpen && <StrategyOverlay onClose={() => setStrategyOpen(false)} data-id="element-27" data-mp="2d484zwlbkavc" />}
      </AnimatePresence>
      <AnimatePresence data-id="element-28" data-mp="287hqhdp5xnc1">
        {journeyOpen && <JourneyOverlay onClose={() => setJourneyOpen(false)} data-id="element-29" data-mp="284ddprxmtj9r" />}
      </AnimatePresence>
    </div>;
}